const createUTCDate = (dateString) => {
  if (!dateString) return null;

  const d = new Date(dateString);
  if (isNaN(d.getTime())) return null;

  // Create a UTC date to avoid timezone shifts
  return new Date(Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate()
  ));
};
export function applyMilestoneSort(result, sortField, sortOrder, historyEntries, users) {
  if (!Array.isArray(result) || !sortField || sortField === 'None') {
    return;
  }
}
// Helper to get a stage's actual date
const getStageActualDate = (initiative, stageName) => {
  if (!initiative || !Array.isArray(initiative.timeline)) return null;

  const stageEntry = initiative.timeline.find(entry => entry.stage === stageName);
  if (!stageEntry?.date?.actual) return null;

  return createUTCDate(stageEntry.date.actual);
};

const getStagePlanDate = (initiative, stageName) => {
  if (!initiative || !Array.isArray(initiative.timeline)) return null;

  const stageEntry = initiative.timeline.find(entry => entry.stage === stageName);
  if (!stageEntry?.date?.plan) return null;

  return createUTCDate(stageEntry.date.plan);
};

const getStageOriginalPlanDate = (initiative, stageName) => {
  if (!initiative || !Array.isArray(initiative.timeline)) return null;

  const stageEntry = initiative.timeline.find(entry => entry.stage === stageName);
  if (!stageEntry?.date?.forecast) return null;

  return createUTCDate(stageEntry.date.forecast);
};

export const L0ActualDate = (initiative) => getStageActualDate(initiative, 'L0');
export const L1ActualDate = (initiative) => getStageActualDate(initiative, 'L1');
export const L2ActualDate = (initiative) => getStageActualDate(initiative, 'L2');
export const L3ActualDate = (initiative) => getStageActualDate(initiative, 'L3');
export const L4ActualDate = (initiative) => getStageActualDate(initiative, 'L4');
export const L5ActualDate = (initiative) => getStageActualDate(initiative, 'L5');

export const L1PlanDate = (initiative) => getStagePlanDate(initiative, 'L1');
export const L2PlanDate = (initiative) => getStagePlanDate(initiative, 'L2');
export const L3PlanDate = (initiative) => getStagePlanDate(initiative, 'L3');
export const L4PlanDate = (initiative) => getStagePlanDate(initiative, 'L4');
export const L5PlanDate = (initiative) => getStagePlanDate(initiative, 'L5');

export const L1OriginalPlanDate = (initiative) => getStageOriginalPlanDate(initiative, 'L1');
export const L2OriginalPlanDate = (initiative) => getStageOriginalPlanDate(initiative, 'L2');
export const L3OriginalPlanDate = (initiative) => getStageOriginalPlanDate(initiative, 'L3');
export const L4OriginalPlanDate = (initiative) => getStageOriginalPlanDate(initiative, 'L4');
export const L5OriginalPlanDate = (initiative) => getStageOriginalPlanDate(initiative, 'L5');

export const SubmittedForL1PlanDate = (initiative) => getStageActualDate(initiative, 'Submitted for L1 Approval');
export const SubmittedForL2PlanDate = (initiative) => getStageActualDate(initiative, 'Submitted for L2 Approval');
export const SubmittedForL3PlanDate = (initiative) => getStageActualDate(initiative, 'Submitted for L3 Approval');
export const SubmittedForL4PlanDate = (initiative) => getStageActualDate(initiative, 'Submitted for L4 Approval');
export const SubmittedForL5PlanDate = (initiative) => getStageActualDate(initiative, 'Submitted for L5 Approval');
export const getCancellationDate = (initiative) => {
  const historyEntry = initiative.history?.find(
    (entry) =>
      (entry.action?.includes('Weekly Status was changed from') &&
        entry.action?.includes('to Cancelled')) ||
      entry.action?.includes('Status update request fully approved by Admin: Status changed to "Cancelled"')
  );
  return historyEntry?.timestamp ? createUTCDate(historyEntry.timestamp) : null;
};

export const getMilestoneStartDate = (milestone) => {
  if (!milestone) return null;
  return createUTCDate(milestone.startDate);
};

export const getActionItemCreatedOnDate = (actionItem) => {
  if (!actionItem) return null;
  return createUTCDate(actionItem.created);
};

export const getActionItemUpdatedOnDate = (actionItem) => {
  if (!actionItem) return null;

  const updatedEvent = actionItem.activity?.find((event) =>
    event.event?.includes(`Workstream action ${actionItem.actionItemNumber} is updated`)
  );

  if (!updatedEvent) return null;
  return createUTCDate(updatedEvent.eventDate);
};

export const getMilestoneEndDate = (milestone) => {
  if (!milestone) return null;
  return createUTCDate(milestone.endDate);
};

// Updated milestone functions with UTC
export const milestoneUpdatedOnDate = (milestone, historyEntries) => {
  if (!historyEntries || !milestone) return null;
  const latestUpdateEntry = historyEntries
    .filter(entry => entry.action?.includes(`Milestone with Number ${milestone.milestoneNumber} has been updated.`))
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0];

  return latestUpdateEntry?.timestamp ? createUTCDate(latestUpdateEntry.timestamp) : null;
};

export const milestoneCreatedOnDate = (milestone, historyEntries) => {
  if (!historyEntries || !milestone) return null;
  const createdHistoryEntry = historyEntries.find(entry => entry.action?.includes(`Milestone #${milestone.name} created`));
  return createdHistoryEntry?.timestamp ? createUTCDate(createdHistoryEntry.timestamp) : null;
};

// FY starts in July
const FY_START_YEAR = 2023;
const FY_END_YEAR = 2027; // last FY: 2027/28

// Generate FY label from a date
export const getFiscalYearLabel = (date) => {
  if (!date) return null;
  const year = date.getFullYear();
  const month = date.getMonth(); // 0-11
  const fyStartYear = month >= 6 ? year : year - 1;
  const fyEndYear = fyStartYear + 1;

  // Restrict to FY 2023/24 – 2027/28
  if (fyStartYear < FY_START_YEAR || fyStartYear > FY_END_YEAR) return null;

  return `FY ${fyStartYear}/${fyEndYear}`;
};

// Fiscal month label: Jul 2023 etc.
export const getFiscalMonthLabel = (date) => {
  if (!date) return null;
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
};

// Fiscal quarter label: Q1 2023/24 etc.
export const getFiscalQuarterLabel = (date) => {
  if (!date) return null;
  const month = date.getMonth(); // 0-11
  const quarter = Math.floor(((month + 6) % 12) / 3) + 1; // Q1 = Jul-Sep
  const fyStartYear = month >= 6 ? date.getFullYear() : date.getFullYear() - 1;
  const fyEndYear = fyStartYear + 1;

  if (fyStartYear < FY_START_YEAR || fyStartYear > FY_END_YEAR) return null;

  return `Q${quarter} ${fyStartYear}/${fyEndYear}`;
};

// Fiscal week label: W1, Jul 2023 etc.
// Simplified: 4 weeks per month
export const getFiscalWeekLabel = (date) => {
  if (!date) return null;
  const month = date.getMonth();
  const fyStartYear = month >= 6 ? date.getFullYear() : date.getFullYear() - 1;
  const monthIndexInFY = (month + 6) % 12; // Jul = 0
  const weekInMonth = Math.ceil(date.getDate() / 7); // 1-5
  const monthLabel = getFiscalMonthLabel(date);

  if (fyStartYear < FY_START_YEAR || fyStartYear > FY_END_YEAR) return null;

  return `W${weekInMonth}, ${monthLabel}`;
};
export const getActionItemCreatedOnYear = (actionItem) => {
  const date = getActionItemCreatedOnDate(actionItem);
  return getFiscalYearLabel(date)
}
export const getActionItemCreatedOnQuarter = (actionItem) => {
  const date = getActionItemCreatedOnDate(actionItem);
  return getFiscalQuarterLabel(date)
}
export const getActionItemCreatedOnMonth = (actionItem) => {
  const date = getActionItemCreatedOnDate(actionItem);
  return getFiscalMonthLabel(date)
}
export const getActionItemCreatedOnWeek = (actionItem) => {
  const date = getActionItemCreatedOnDate(actionItem);
  return getFiscalWeekLabel(date)
}
export const getActionItemUpdatedOnYear = (actionItem) => {
  const date = getActionItemUpdatedOnDate(actionItem);
  return getFiscalYearLabel(date)
}
export const getActionItemUpdatedOnWeek = (actionItem) => {
  const date = getActionItemUpdatedOnDate(actionItem);
  return getFiscalWeekLabel(date)
}
export const getActionItemUpdatedOnMonth = (actionItem) => {
  const date = getActionItemUpdatedOnDate(actionItem);
  return getFiscalMonthLabel(date)
}
export const getActionItemUpdatedOnQuarter = (actionItem) => {
  const date = getActionItemUpdatedOnDate(actionItem);
  return getFiscalQuarterLabel(date)
}
export const getMilestoneUpdatedOnYear = (milestone, history) => {
  const date = milestoneUpdatedOnDate(milestone, history);
  return getFiscalYearLabel(date);
};
export const getMilestoneUpdatedOnQuarter = (milestone, history) => {
  const date = milestoneUpdatedOnDate(milestone, history);
  return getFiscalQuarterLabel(date);
};
export const getMilestoneUpdatedOnMonth = (milestone, history) => {
  const date = milestoneUpdatedOnDate(milestone, history);
  return getFiscalMonthLabel(date);
};
export const getMilestoneUpdatedOnWeek = (milestone, history) => {
  const date = milestoneUpdatedOnDate(milestone, history);
  return getFiscalWeekLabel(date);
};

export const getMilestoneCreatedOnYear = (milestone, history) => {
  const date = milestoneCreatedOnDate(milestone, history);
  return getFiscalYearLabel(date);
};
export const getMilestoneCreatedOnQuarter = (milestone, history) => {
  const date = milestoneCreatedOnDate(milestone, history);
  return getFiscalQuarterLabel(date);
};
export const getMilestoneCreatedOnMonth = (milestone, history) => {
  const date = milestoneCreatedOnDate(milestone, history);
  return getFiscalMonthLabel(date);
};
export const getMilestoneCreatedOnWeek = (milestone, history) => {
  const date = milestoneCreatedOnDate(milestone, history);
  return getFiscalWeekLabel(date);
};
export const getMilestoneStartYear = (milestone) => {
  const date = getMilestoneStartDate(milestone);
  return getFiscalYearLabel(date);
};
export const getMilestoneStartQuarter = (milestone) => {
  const date = getMilestoneStartDate(milestone);
  return getFiscalQuarterLabel(date);
};
export const getMilestoneStartMonth = (milestone) => {
  const date = getMilestoneStartDate(milestone);
  return getFiscalMonthLabel(date);
};
export const getMilestoneStartWeek = (milestone) => {
  const date = getMilestoneStartDate(milestone);
  return getFiscalWeekLabel(date);
};
export const getMilestoneEndYear = (milestone) => {
  const date = getMilestoneEndDate(milestone);
  return getFiscalYearLabel(date);
};
export const getMilestoneEndQuarter = (milestone) => {
  const date = getMilestoneEndDate(milestone);
  return getFiscalQuarterLabel(date);
};
export const getMilestoneEndMonth = (milestone) => {
  const date = getMilestoneEndDate(milestone);
  return getFiscalMonthLabel(date);
};
export const getMilestoneEndWeek = (milestone) => {
  const date = getMilestoneEndDate(milestone);
  return getFiscalWeekLabel(date);
};
export const getCancellationYear = (initiative) => {
  const date = getCancellationDate(initiative);
  return getFiscalYearLabel(date)
}
export const getCancellationQuarter = (initiative) => {
  const date = getCancellationDate(initiative);
  return getFiscalQuarterLabel(date)
}
export const getCancellationMonth = (initiative) => {
  const date = getCancellationDate(initiative);
  return getFiscalMonthLabel(date)
}
export const getCancellationWeek = (initiative) => {
  const date = getCancellationDate(initiative);
  return getFiscalWeekLabel(date)
}
export const getStageActualYear = (initiative, stage) => {
  const date = getStageActualDate(initiative, stage);
  return getFiscalYearLabel(date);
};

export const getStageActualQuarter = (initiative, stage) => {
  const date = getStageActualDate(initiative, stage);
  return getFiscalQuarterLabel(date);
};

export const getStageActualMonth = (initiative, stage) => {
  const date = getStageActualDate(initiative, stage);
  return getFiscalMonthLabel(date);
};

export const getStageActualWeek = (initiative, stage) => {
  const date = getStageActualDate(initiative, stage);
  return getFiscalWeekLabel(date);
};

// Helper functions for plan dates
export const getStagePlanYear = (initiative, stageName) => {
  const date = getStagePlanDate(initiative, stageName);
  return getFiscalYearLabel(date);
};

export const getStagePlanQuarter = (initiative, stageName) => {
  const date = getStagePlanDate(initiative, stageName);
  return getFiscalQuarterLabel(date);
};

export const getStagePlanMonth = (initiative, stageName) => {
  const date = getStagePlanDate(initiative, stageName);
  return getFiscalMonthLabel(date);
};

export const getStagePlanWeek = (initiative, stageName) => {
  const date = getStagePlanDate(initiative, stageName);
  return getFiscalWeekLabel(date);
};
export const getStageOriginalPlanYear = (initiative, stageName) => {
  const date = getStageOriginalPlanDate(initiative, stageName);
  return getFiscalYearLabel(date);
};

export const getStageOriginalPlanQuarter = (initiative, stageName) => {
  const date = getStageOriginalPlanDate(initiative, stageName);
  return getFiscalQuarterLabel(date);
};

export const getStageOriginalPlanMonth = (initiative, stageName) => {
  const date = getStageOriginalPlanDate(initiative, stageName);
  return getFiscalMonthLabel(date);
};

export const getStageOriginalPlanWeek = (initiative, stageName) => {
  const date = getStageOriginalPlanDate(initiative, stageName);
  // Corrected function name from getFiscalWeekLabelS to getFiscalWeekLabel
  return getFiscalWeekLabel(date);
};
export const getQualitativeKPI = (initiative) => {
  if (!initiative?.KPI || !Array.isArray(initiative.KPI) || initiative.KPI.length === 0) {
    return '<not set>';
  }

  const kpi = initiative.KPI.find((k) => k.isFinancial);
  return kpi ? 'No' : 'Yes';
};
export const getSegmentalPBT = (segmental) => {
  if (!segmental) {
    return '<not set>';
  }
  return segmental ? 'Yes' : 'No';
}